import { useEffect, useRef, useState } from 'react'
import { tokens } from '@/theme'

/**
 * The funnel itself — six slabs narrowing downward, one per stage, with the
 * selected stage lifted and lit.
 *
 * Ported from the `orion-funnel` custom element in the Claude Design
 * project, with two changes the app requires: three.js comes from the
 * bundle's own lazy chunk rather than a CDN `import()`, and selection is a
 * React prop pair instead of a bubbling `CustomEvent`.
 *
 * It degrades the same three ways `OrionScene` does — flat SVG before the
 * chunk lands and wherever WebGL is unavailable, a single rendered frame
 * under `prefers-reduced-motion`, full ambient motion otherwise. The
 * difference is that the flat fallback here is a real funnel with the
 * active slab marked, not the brand mark: this graphic carries which stage
 * is selected, so the fallback has to carry it too.
 *
 * The canvas stays `aria-hidden` and is never the only route to anything.
 * Every stage it draws is also a button in the list beside it, which is
 * what keyboard and screen-reader users drive.
 */

interface Props {
  /** Index of the lit slab. 0 is the widest — Idea. */
  active: number
  /** Clicking a slab selects that stage. Omit for a decorative funnel. */
  onSelect?: (index: number) => void
  /** Rendered height in px. Width fills the host column. */
  height?: number
  /** Slab count. Six, unless `ref.InitiativeStage` grows. */
  count?: number
}

// three.js wants numeric colours. The ramp runs dark ink at the wide top to
// gold as the funnel narrows, with Operate in the brand teal — it is out of
// the funnel, not further down it.
const GOLD = 0x998242
const TEAL = 0x6dc1c9
const ACTIVE_LAST = 0x2c6b62
const ACTIVE = 0xc2a75c
const RAMP = [0x3c372c, 0x4d4635, 0x63583c, 0x7c6c44, 0x998242, 0x2c6b62]

/** Slab thickness, the gap between slabs, and the top/bottom radii. */
const TH = 0.52
const GAP = 0.46
const R_TOP = 3.05
const R_BOT = 1.0

/** One stage: its slab, the gate ring on top, and the state the loop eases. */
interface Slab {
  mesh: import('three').Mesh
  material: import('three').MeshStandardMaterial
  edge: import('three').Mesh
  edgeMaterial: import('three').MeshBasicMaterial
  /** Resting y, before selection lift and drift. */
  base: number
  isLast: boolean
  ramp: number
  /** Offsets the idle bob so the stack does not breathe in unison. */
  phase: number
}

export function OrionFunnel({ active, onSelect, height = 410, count = 6 }: Props) {
  const hostRef = useRef<HTMLDivElement>(null)
  const [webglReady, setWebglReady] = useState(false)

  // Selection and the click handler are read through refs so changing either
  // does not tear down and rebuild the scene — a rebuild on every click
  // would drop the eased transition the slabs exist to show.
  const activeRef = useRef(active)
  activeRef.current = active
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  /** Set by the effect once the scene exists; snaps state and draws one frame. */
  const drawOnceRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    let cancelled = false
    let teardown: (() => void) | null = null

    void (async () => {
      let THREE: typeof import('three')
      try {
        THREE = await import('three')
      } catch {
        // Chunk failed to load (offline, blocked) — the SVG stays.
        return
      }
      if (cancelled) return

      const canvas = document.createElement('canvas')
      Object.assign(canvas.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        display: 'block',
      })
      canvas.setAttribute('aria-hidden', 'true')

      let renderer: import('three').WebGLRenderer
      try {
        renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true })
      } catch {
        // No WebGL context available — keep the SVG fallback.
        return
      }
      if (cancelled) {
        renderer.dispose()
        return
      }

      host.appendChild(canvas)
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2))

      const scene = new THREE.Scene()
      const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100)
      camera.position.set(0, 2.4, 12.8)
      camera.lookAt(0, -0.3, 0)

      scene.add(new THREE.AmbientLight(0xffffff, 0.6))
      const key = new THREE.DirectionalLight(0xfff0cf, 3.0)
      key.position.set(5, 8, 7)
      scene.add(key)
      const rim = new THREE.DirectionalLight(TEAL, 1.15)
      rim.position.set(-7, -2, -5)
      scene.add(rim)
      const fill = new THREE.DirectionalLight(GOLD, 0.9)
      fill.position.set(-5, 3, 6)
      scene.add(fill)
      const under = new THREE.PointLight(GOLD, 12, 9, 2)
      under.position.set(0, -2.6, 0)
      scene.add(under)

      // pivot = pointer parallax, group = the funnel's own drift.
      const pivot = new THREE.Group()
      scene.add(pivot)
      const group = new THREE.Group()
      pivot.add(group)

      const total = count * TH + (count - 1) * GAP
      const top = total / 2
      const radiusAt = (y: number) => R_BOT + ((y + total / 2) / total) * (R_TOP - R_BOT)

      const slabs: Slab[] = []
      for (let i = 0; i < count; i++) {
        const yTop = top - i * (TH + GAP)
        const yBot = yTop - TH
        const isLast = i === count - 1
        const ramp = RAMP[i % RAMP.length]!
        const material = new THREE.MeshStandardMaterial({
          color: ramp,
          metalness: 0.8,
          roughness: 0.24,
          emissive: isLast ? TEAL : GOLD,
          emissiveIntensity: 0.04,
          transparent: true,
          opacity: 0.95,
        })
        const mesh = new THREE.Mesh(
          new THREE.CylinderGeometry(radiusAt(yTop), radiusAt(yBot), TH, 96, 1, false),
          material,
        )
        mesh.position.y = (yTop + yBot) / 2
        mesh.userData.index = i
        group.add(mesh)

        // Hairline gold ring on the top face — the gate between two stages.
        const edgeMaterial = new THREE.MeshBasicMaterial({
          color: GOLD,
          transparent: true,
          opacity: 0.5,
        })
        const edge = new THREE.Mesh(
          new THREE.TorusGeometry(radiusAt(yTop), 0.02, 10, 160),
          edgeMaterial,
        )
        edge.rotation.x = Math.PI / 2
        edge.position.y = yTop
        group.add(edge)

        slabs.push({
          mesh,
          material,
          edge,
          edgeMaterial,
          base: (yTop + yBot) / 2,
          isLast,
          ramp,
          phase: i * 0.7,
        })
      }

      const resize = () => {
        const w = host.clientWidth || 1
        const h = host.clientHeight || 1
        renderer.setSize(w, h, false)
        camera.aspect = w / h
        camera.updateProjectionMatrix()
      }
      resize()
      const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
      ro?.observe(host)

      // Pointer: parallax, hover highlight and picking.
      const raycaster = new THREE.Raycaster()
      const ndc = new THREE.Vector2()
      let tx = 0
      let ty = 0
      let px = 0
      let py = 0
      let hover = -1

      const pickAt = (event: PointerEvent): number => {
        const rect = host.getBoundingClientRect()
        ndc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        ndc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1
        raycaster.setFromCamera(ndc, camera)
        const hit = raycaster.intersectObjects(
          slabs.map(s => s.mesh),
          false,
        )[0]
        return hit ? (hit.object.userData.index as number) : -1
      }

      const interactive = () => onSelectRef.current !== undefined

      const onMove = (event: PointerEvent) => {
        const rect = host.getBoundingClientRect()
        tx = ((event.clientX - rect.left) / rect.width - 0.5) * 2
        ty = ((event.clientY - rect.top) / rect.height - 0.5) * 2
        if (!interactive()) return
        hover = pickAt(event)
        host.style.cursor = hover >= 0 ? 'pointer' : 'default'
      }
      const onLeave = () => {
        tx = 0
        ty = 0
        hover = -1
        host.style.cursor = 'default'
      }
      const onDown = (event: PointerEvent) => {
        const select = onSelectRef.current
        if (!select) return
        const index = pickAt(event)
        if (index >= 0) select(index)
      }
      host.addEventListener('pointermove', onMove, { passive: true })
      host.addEventListener('pointerleave', onLeave)
      host.addEventListener('pointerdown', onDown)

      const reduced =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches

      /**
       * One step of slab state. `snap` jumps straight to the target instead
       * of easing toward it — used for the single frame drawn under reduced
       * motion, where there is no next frame to ease into.
       */
      const stepSlabs = (t: number, snap: boolean) => {
        const ease = (from: number, to: number, k: number) => (snap ? to : from + (to - from) * k)
        slabs.forEach((s, i) => {
          const on = i === activeRef.current
          const hot = i === hover
          const targetScale = on ? 1.06 : hot ? 1.03 : 1
          s.mesh.scale.x = ease(s.mesh.scale.x, targetScale, 0.12)
          s.mesh.scale.z = s.mesh.scale.x
          const lift = (on ? 0.13 : 0) + Math.sin(t * 0.7 + s.phase) * 0.035
          s.mesh.position.y = ease(s.mesh.position.y, s.base + lift, 0.12)
          s.edge.position.y = s.mesh.position.y + TH / 2
          s.edge.scale.x = s.mesh.scale.x
          s.edge.scale.y = s.mesh.scale.x
          s.material.color.setHex(on ? (s.isLast ? ACTIVE_LAST : ACTIVE) : s.ramp)
          s.material.emissiveIntensity = ease(s.material.emissiveIntensity, on ? 0.28 : 0.05, 0.15)
          s.material.opacity = ease(s.material.opacity, on ? 1 : hot ? 0.97 : 0.9, 0.15)
          s.edgeMaterial.opacity = ease(s.edgeMaterial.opacity, on ? 0.95 : hot ? 0.6 : 0.35, 0.15)
        })
      }

      let raf = 0
      const t0 = performance.now()
      const frame = (snap: boolean) => {
        const t = (performance.now() - t0) / 1000
        px += (tx - px) * 0.05
        py += (ty - py) * 0.05
        stepSlabs(t, snap)
        group.rotation.y = Math.sin(t * 0.13) * 0.26 + px * 0.35
        pivot.rotation.x = 0.05 + py * 0.1 + Math.sin(t * 0.3) * 0.025
        group.position.y = Math.sin(t * 0.5) * 0.05
        renderer.render(scene, camera)
      }
      const tick = () => {
        frame(false)
        raf = requestAnimationFrame(tick)
      }
      if (reduced) frame(true)
      else tick()

      // Reduced motion has no loop to pick the change up, so selection
      // redraws explicitly.
      drawOnceRef.current = reduced ? () => frame(true) : null

      setWebglReady(true)

      teardown = () => {
        cancelAnimationFrame(raf)
        ro?.disconnect()
        host.removeEventListener('pointermove', onMove)
        host.removeEventListener('pointerleave', onLeave)
        host.removeEventListener('pointerdown', onDown)
        host.style.cursor = ''
        drawOnceRef.current = null
        // Geometries and materials are GPU allocations; React unmounting the
        // host doesn't free them.
        scene.traverse(obj => {
          const withGeo = obj as { geometry?: { dispose(): void }; material?: unknown }
          withGeo.geometry?.dispose()
          const material = withGeo.material
          const materials = Array.isArray(material) ? material : material ? [material] : []
          for (const m of materials) (m as { dispose(): void }).dispose()
        })
        renderer.dispose()
        canvas.remove()
        setWebglReady(false)
      }
    })()

    return () => {
      cancelled = true
      teardown?.()
    }
  }, [count])

  useEffect(() => {
    drawOnceRef.current?.()
  }, [active])

  return (
    <div ref={hostRef} style={{ position: 'relative', height, width: '100%' }}>
      <FlatFunnel active={active} count={count} faded={webglReady} />
    </div>
  )
}

/**
 * The flat face of the funnel: stacked trapezoids, narrowing downward, the
 * active one gold. Sized in the viewBox so it scales to whatever height the
 * host was given.
 */
function FlatFunnel({ active, count, faded }: { active: number; count: number; faded: boolean }) {
  const slabH = 100 / (count * 1.9)
  const gap = slabH * 0.9
  const rows = Array.from({ length: count }, (_, i) => {
    const y = i * (slabH + gap)
    const wTop = 92 - (i / count) * 58
    const wBot = 92 - ((i + 1) / count) * 58
    return { i, y, wTop, wBot }
  })

  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: faded ? 0 : 1,
        transition: `opacity ${tokens.durSlow} ${tokens.easeStandard}`,
        pointerEvents: 'none',
      }}
    >
      {rows.map(({ i, y, wTop, wBot }) => (
        <polygon
          key={i}
          points={`${50 - wTop / 2},${y} ${50 + wTop / 2},${y} ${50 + wBot / 2},${y + slabH} ${50 - wBot / 2},${y + slabH}`}
          fill={i === active ? tokens.gold : tokens.inkPanel2}
          stroke={tokens.gold}
          strokeWidth={0.4}
          opacity={i === active ? 1 : 0.55}
        />
      ))}
    </svg>
  )
}
